import React from 'react';
import * as d3 from 'd3';
import { useResponsiveD3, useD3Tooltip } from '../../../hooks/useD3';

const OceanWasteChart = ({ data }) => {
  const { showTooltip, hideTooltip } = useD3Tooltip();

  const renderChart = (svg, { width, height }) => {
    const margin = { top: 30, right: 150, bottom: 30, left: 30 }; // Increased right margin for legend
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    const radius = Math.min(chartWidth, chartHeight) / 2;

    // Create main group centered
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Process ocean waste data
    const oceanData = data || {
      wetWaste: 0,
      dryWaste: 0,
      mixedWaste: 0,
      sanitaryWaste: 0
    };

    // Check if there's any data to display
    const hasData = oceanData.wetWaste > 0 || oceanData.dryWaste > 0 || 
                   oceanData.mixedWaste > 0 || oceanData.sanitaryWaste > 0;

    const pieData = [
      { label: 'Wet Waste', value: oceanData.wetWaste, color: '#2563EB' },
      { label: 'Dry Waste', value: oceanData.dryWaste, color: '#DC2626' },
      { label: 'Mixed Waste', value: oceanData.mixedWaste, color: '#059669' },
      { label: 'Sanitary Waste', value: oceanData.sanitaryWaste, color: '#7C3AED' }
    ];

    // Filter only non-zero values for pie chart
    const filteredPieData = pieData.filter(d => d.value > 0);

    // Create pie layout
    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(radius * 0.4)
      .outerRadius(radius * 0.8);

    const hoverArc = d3.arc()
      .innerRadius(radius * 0.4)
      .outerRadius(radius * 0.85);

    // Create pie slices
    const slices = g.selectAll('.slice')
      .data(pie(filteredPieData))
      .enter().append('g')
      .attr('class', 'slice');

    // Add paths
    slices.append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', hoverArc);
        
        const percentage = ((d.value / d3.sum(filteredPieData, p => p.value)) * 100).toFixed(1);
        showTooltip(`${d.data.label}<br>Count: ${d.data.value}<br>Percentage: ${percentage}%`, event);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc);
        hideTooltip();
      })
      .transition()
      .duration(800)
      .attrTween('d', function(d) {
        const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
        return function(t) {
          return arc(interpolate(t));
        };
      });

    // Add labels on slices (only if slice is large enough)
    slices.append('text')
      .attr('transform', d => {
        const centroid = arc.centroid(d);
        const angle = (d.startAngle + d.endAngle) / 2;
        const isLargeSlice = (d.endAngle - d.startAngle) > 0.3; // Only show label if slice is large enough
        return isLargeSlice ? `translate(${centroid})` : null;
      })
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.8)')
      .text(d => {
        const isLargeSlice = (d.endAngle - d.startAngle) > 0.3;
        return isLargeSlice ? d.data.value : '';
      })
      .style('opacity', 0)
      .transition()
      .delay(800)
      .duration(400)
      .style('opacity', 1);

    // Add center text
    const centerText = g.append('g').attr('class', 'center-text');
    
    centerText.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.5em')
      .style('font-size', '24px')
      .style('font-weight', 'bold')
      .style('fill', '#1F2937')
      .text(d3.sum(filteredPieData, d => d.value));

    centerText.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.2em')
      .style('font-size', '13px')
      .style('fill', '#6B7280')
      .style('font-weight', '500')
      .text('Total Collections');

    // Add legend with better positioning and styling
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width - 130}, ${Math.max(20, height / 2 - (filteredPieData.length * 12.5))})`); // Position legend to the right of the chart, centered vertically

    const legendItems = legend.selectAll('.legend-item')
      .data(filteredPieData)
      .enter().append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 25})`);

    legendItems.append('circle')
      .attr('r', 7)
      .attr('fill', d => d.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    legendItems.append('text')
      .attr('x', 15)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .style('font-size', '13px')
      .style('fill', '#374151')
      .style('font-weight', '500')
      .text(d => `${d.label} (${d.value})`);
  };

  const { containerRef, svgRef } = useResponsiveD3(
    renderChart, 
    [data], 
    2 // Increased aspect ratio for better sizing
  );

  return (
    <div ref={containerRef} className="w-full h-full">
      <svg ref={svgRef} className="w-full h-full"></svg>
    </div>
  );
};

export default OceanWasteChart;